import { Controller } from "@hotwired/stimulus"

// ui--media-library — selection bookkeeping and client-side filtering for
// Ui::MediaLibraryComponent.
//
// What this controller is NOT responsible for: making selection work. The
// cards hold real <input type="checkbox"> / <input type="radio"> elements with
// a name, so a library inside a <form> submits correctly with JavaScript
// disabled. Everything here is the layer on top — the running count, the
// filter box, clearing, and the event the picker listens to.
//
// Targets:
//   card      - one asset tile (the outer div)
//   input     - the checkbox/radio inside a card
//   search    - the filter box
//   filter    - the type <select>
//   count     - the "n selected" line
//   noResults - the line shown when a client-side filter matches nothing
//   grid      - the grid itself
//   form      - the toolbar form, in server-search mode only
//
// Values:
//   selection      - "none" | "single" | "multiple"
//   serverSearch   - true when the server does the filtering; disables the
//                    client-side filter entirely rather than running both
//   selectedLabel  - "%{count} selected", already pluralised for 0 by Rails;
//                    the count is substituted client-side (see _countText)
//   noResultsLabel - unused text kept for hosts that re-render the line
//
// Events:
//   ui--media-library:change - { detail: { ids, filenames } } on every
//                              selection change. Ui::MediaPickerModalComponent
//                              is the consumer.
export default class extends Controller {
  static targets = [
    "card",
    "input",
    "search",
    "filter",
    "count",
    "noResults",
    "grid",
    "form",
  ]

  static values = {
    selection: { type: String, default: "none" },
    serverSearch: { type: Boolean, default: false },
    selectedLabel: { type: String, default: "%{count} selected" },
    noResultsLabel: { type: String, default: "" },
  }

  connect() {
    this._updateCount()
  }

  // A radio group already clears its siblings; a checkbox grid does not need
  // clearing. So this only has to update the count and tell anyone listening —
  // which is why it is safe to bind to `change` on the inputs rather than
  // intercepting clicks on the cards.
  toggle() {
    this._updateCount()
    this._emit()
  }

  clear() {
    this.inputTargets.forEach((input) => {
      input.checked = false
    })
    this._updateCount()
    this._emit()
  }

  // Server-search mode: submit the toolbar form, which is a GET into the turbo
  // frame around the grid.
  submit() {
    if (this.hasFormTarget) this.formTarget.requestSubmit()
  }

  // Client mode: hide cards whose filename does not contain the query, and
  // whose variant does not match the type filter. Hidden rather than removed
  // so a selected card that scrolls out of the filter keeps its checked input
  // — filtering must never silently deselect.
  filter() {
    if (this.serverSearchValue) return

    const query = this.hasSearchTarget
      ? this.searchTarget.value.trim().toLowerCase()
      : ""
    const type = this.hasFilterTarget ? this.filterTarget.value : ""

    let visible = 0

    this.cardTargets.forEach((card) => {
      const filename = (card.dataset.mediaAssetFilename || "").toLowerCase()
      const variant = card.dataset.mediaAssetVariant || ""
      const matches =
        (query === "" || filename.includes(query)) &&
        (type === "" || variant === type)

      card.hidden = !matches
      if (matches) visible += 1
    })

    if (this.hasNoResultsTarget) {
      this.noResultsTarget.classList.toggle("hidden", visible > 0)
    }
    if (this.hasGridTarget) {
      this.gridTarget.classList.toggle("hidden", visible === 0)
    }
  }

  get selectedIds() {
    return this.inputTargets.filter((i) => i.checked).map((i) => i.value)
  }

  get selectedFilenames() {
    return this.inputTargets
      .filter((i) => i.checked)
      .map((i) => this._cardFor(i)?.dataset.mediaAssetFilename || "")
  }

  _cardFor(input) {
    return input.closest("[data-media-asset-id]")
  }

  _updateCount() {
    if (!this.hasCountTarget) return
    this.countTarget.textContent = this._countText(this.selectedIds.length)
  }

  // `selectedLabel` is the raw "%{count} selected" string, NOT a rendered one:
  // the count changes in the browser, and a string Rails had already
  // interpolated would have to be un-interpolated here. It is deliberately not
  // a Rails pluralisation for the same reason — the browser cannot pick a
  // plural form, so the copy is written to read correctly at any count.
  _countText(count) {
    const label = this.selectedLabelValue || "%{count} selected"
    if (label.includes("%{count}")) return label.replace("%{count}", count)
    return label.replace(/\d+/, String(count))
  }

  _emit() {
    this.dispatch("change", {
      detail: { ids: this.selectedIds, filenames: this.selectedFilenames },
    })
  }
}
